// ── COACHING: requests & confirmation chase list ─────────────────────
// The board answers "who goes where"; this answers "who still owes me an
// answer". One row per request rather than per session, because Lori works a
// family at a time and a family's sessions share one email and one
// confirmation.
//
// No bulk actions by design. She sends and nudges one family at a time, which
// keeps every message about one specific person and one specific time — and
// avoids having to decide what a batch email says when its recipients are at
// different stages.
import { apiFetch } from '../core/api.js';
import { COACHING_EVENTS } from '../core/state.js';
import { toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT } from '../core/routing.js';

let ccEventId = null;
let ccData = null;
let ccFilter = 'all';

const STATUS = {
  unplaced:            { label: 'Unplaced',           cls: 'pill-off' },
  draft:               { label: 'Draft',              cls: 'pill-gold' },
  'partial-draft':     { label: 'Half placed',        cls: 'pill-amber' },
  sent:                { label: 'Awaiting confirm',   cls: 'pill-amber' },
  'partial-sent':      { label: 'Half sent',          cls: 'pill-amber' },
  confirmed:           { label: 'Confirmed & paid',   cls: 'pill-active' },
  'partial-confirmed': { label: 'Half confirmed',     cls: 'pill-amber' }
};

const FILTERS = [
  ['all', 'Everyone'],
  ['unplaced', 'Unplaced'],
  ['draft', 'Draft'],
  ['sent', 'Awaiting confirm'],
  ['confirmed', 'Confirmed']
];

function chaseEvents() {
  return COACHING_EVENTS.filter(e => !e.archived).slice().sort((a, b) => a.eventDate.localeCompare(b.eventDate));
}

function defaultEventId() {
  const events = chaseEvents();
  if (!events.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (events.find(e => e.eventDate >= today) || events[events.length - 1]).id;
}

// A row matches a filter by the stage it's at, half-done states included —
// "Awaiting confirm" should surface a family whose second session is still
// sitting unsent, not hide them behind an exact-match label.
const matches = (row, mode) => mode === 'all' || row.status === mode || row.status === 'partial-' + mode;

// Days since the first of this family's times went out, which is the only
// number that says who has been sitting on it longest.
function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso.replace(' ', 'T')).getTime()) / 86400000);
}

async function loadChase(eventId) {
  ccEventId = eventId;
  try {
    ccData = await apiFetch('/coaching/events/' + eventId + '/chase');
  } catch (e) {
    ccData = null;
    toastError(e.message);
  }
  redraw();
}

function redraw() {
  const main = document.getElementById('main');
  if (!main) return;
  main.innerHTML = VIEWS['a-coaching-requests']();
  wireChase();
}

VIEWS['a-coaching-requests'] = () => {
  const events = chaseEvents();
  if (!events.length) {
    return `<div class="page-head"><h2 class="serif">Coaching <em>Requests</em></h2></div>
      <div class="card"><p class="empty">No coaching events yet.</p></div>`;
  }
  if (!ccData) {
    return `<div class="page-head"><h2 class="serif">Coaching <em>Requests</em></h2></div>
      <div class="card"><p class="empty">Loading…</p></div>`;
  }

  const rows = ccData.rows || [];
  const ev = ccData.event;
  const counts = mode => rows.filter(r => matches(r, mode)).length;
  const confirmedSessions = rows.reduce((n, r) => n + (r.confirmed || 0), 0);
  const awaiting = counts('sent');
  const stale = rows.filter(r => matches(r, 'sent') && daysSince(r.sentAt) >= 3).length;
  const unplaced = counts('unplaced');
  const openSessions = rows.reduce((n, r) => n + (r.sessions - r.placed), 0);

  const switcher = events.map(e => `<button data-action="cc-switch" data-event="${e.id}" style="padding:4px 14px;background:transparent;border:1px solid ${e.id === ccEventId ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.18)'};color:${e.id === ccEventId ? 'var(--gold-light)' : 'var(--gold)'};font-size:12px;border-radius:7px;cursor:pointer;font-family:inherit">${esc(e.name)} · ${new Date(e.eventDate + 'T00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</button>`).join('');

  const stat = (label, value, detail, accent) => `<div class="stat" style="--stat-accent:${accent}">
    <div class="lab">${label}</div><div class="val" style="color:${accent}">${value}</div><div class="det">${detail}</div></div>`;

  const tabs = FILTERS.map(([mode, label]) => `<button data-action="cc-filter" data-mode="${mode}" style="border:1px solid ${ccFilter === mode ? 'var(--gold)' : 'var(--border-soft)'};color:${ccFilter === mode ? 'var(--gold-light)' : 'var(--muted)'};background:${ccFilter === mode ? 'rgba(201,168,76,0.12)' : 'transparent'};border-radius:7px;padding:6px 14px;font-size:13px;font-family:inherit;cursor:pointer">${label} (${counts(mode)})</button>`).join('');

  const body = rows.filter(r => matches(r, ccFilter)).map(r => {
    const st = STATUS[r.status] || STATUS.unplaced;
    const days = daysSince(r.sentAt);
    const chase = matches(r, 'sent') && days !== null && days >= 3;
    return `<tr>
      <td><div style="color:var(--gold-light)">${esc(r.performerName)}</div></td>
      <td><div>${esc(r.familyName)}</div><div class="sub">${esc(r.email)}</div></td>
      <td>${r.sessions === 1 ? '1 session' : r.sessions + ' sessions'}${r.placed && r.placed < r.sessions ? `<div class="sub">${r.placed} placed</div>` : ''}</td>
      <td>${r.times.length ? r.times.map(t => `<div style="font-size:12.5px">${esc(t)}</div>`).join('') : '<span class="sub">—</span>'}</td>
      <td><span class="pill ${chase ? 'pill-due' : st.cls}">${chase ? 'Sent ' + days + ' days ago' : st.label}</span></td>
      <td style="text-align:right;white-space:nowrap">${rowAction(r)}</td>
    </tr>`;
  }).join('');

  return `
    <div class="page-head">
      <span class="eyebrow">Coaching · Requests</span>
      <h2 class="serif">Coaching <em>requests</em></h2>
    </div>
    <div style="display:flex;gap:8px;margin-bottom:20px;flex-wrap:wrap">${switcher}</div>
    <div class="stat-row" style="grid-template-columns:repeat(4,minmax(0,1fr))">
      ${stat('Requests', rows.length, openSessions + ' sessions still open', 'var(--cream)')}
      ${stat('Still unplaced', unplaced, unplaced === 1 ? '1 performer' : unplaced + ' performers', 'var(--cream)')}
      ${stat('Awaiting confirm', awaiting, stale ? stale + ' sent over 3 days ago' : 'all recent', '#fbbf24')}
      ${stat('Confirmed &amp; paid', fmt$(confirmedSessions * ev.price), confirmedSessions + (confirmedSessions === 1 ? ' session' : ' sessions'), 'var(--green)')}
    </div>
    <div class="card">
      <div style="display:flex;gap:10px;margin-bottom:18px;flex-wrap:wrap">${tabs}</div>
      <div class="tbl-wrap"><table>
        <tr><th>Performer</th><th>Family</th><th>Asked for</th><th>Assigned</th><th>Status</th><th></th></tr>
        ${body || '<tr><td colspan="6"><p class="empty">Nobody in this view.</p></td></tr>'}
      </table></div>
    </div>`;
};

function rowAction(r) {
  if (r.status === 'unplaced') {
    return '<button class="btn-ghost btn-sm" data-action="cc-board">Open board</button>';
  }
  if (r.status.endsWith('draft')) {
    return `<button class="btn-ghost btn-sm" style="border-color:rgba(201,168,76,0.5);color:var(--gold-light)" data-action="cc-send" data-account="${r.accountId}">Send</button>`;
  }
  if (r.status.endsWith('sent')) {
    return `<button class="btn-ghost btn-sm" data-action="cc-send" data-account="${r.accountId}" data-resend="1">Nudge</button>`;
  }
  return '';
}

VIEW_INIT['a-coaching-requests'] = () => {
  const id = ccEventId || defaultEventId();
  if (!id) return;
  if (!ccData || ccData.event.id !== id) loadChase(id);
  else wireChase();
};

function wireChase() {
  const main = document.getElementById('main');
  if (!main) return;
  const on = (action, fn) => main.querySelectorAll(`[data-action="${action}"]`).forEach(el => {
    el.addEventListener('click', () => fn(el));
  });
  on('cc-switch', el => loadChase(Number(el.dataset.event)));
  on('cc-filter', el => { ccFilter = el.dataset.mode; redraw(); });
  on('cc-board', () => window.route('a-coaching-board'));
  on('cc-send', el => sendToFamily(Number(el.dataset.account), el.dataset.resend === '1'));
}

async function sendToFamily(accountId, resend) {
  try {
    const res = await apiFetch('/coaching/send', {
      method: 'POST',
      body: JSON.stringify({ eventId: ccEventId, accountId, resend: !!resend })
    });
    await loadChase(ccEventId);
    toast(resend ? 'Reminder sent' : (res.sent === 1 ? 'Time sent' : res.sent + ' times sent'));
  } catch (e) {
    toastError(e.message);
  }
}
